import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiPlus } from "react-icons/fi";
import toast from "react-hot-toast";
import { PageHeader, StateBlock, Skeleton, ConfirmDialog } from "../../components/UI";
import Dialog from "../../components/UI/Dialog";
import MapThumb from "../../components/UI/MapThumb";
import { useLanguage } from "../../contexts/LanguageContext";
import { apiFetch } from "../../lib/api";
import { Action, Field, Select, TextArea, FormField } from "../MVP/shared";
import { Chip, Err, Rows, Surface, Toolbar, adminErrorText } from "./ui";

type Map = {
  id: string;
  name: string;
  display_name: string;
  description: string;
  image_url: string;
  workshop_id: string;
  category: string;
  status: string;
  min_players: number;
  max_players: number;
  game_modes: string[];
  is_official: boolean;
};
const EMPTY: Omit<Map, "id"> = {
  name: "",
  display_name: "",
  description: "",
  image_url: "",
  workshop_id: "",
  category: "main",
  status: "active",
  min_players: 2,
  max_players: 10,
  game_modes: ["Competitive"],
  is_official: true,
};
const toMap = (r: Record<string, unknown>): Map => ({
  id: String(r.id),
  name: String(r.name || ""),
  display_name: String(r.display_name || ""),
  description: String(r.description || ""),
  image_url: String(r.image_url || ""),
  workshop_id: String(r.workshop_id || ""),
  category: String(r.category || "main"),
  status: String(r.status || "active"),
  min_players: Number(r.min_players) || 2,
  max_players: Number(r.max_players) || 10,
  game_modes: Array.isArray(r.game_modes) ? (r.game_modes as string[]) : ["Competitive"],
  is_official: Boolean(r.is_official),
});

const List = styled(Rows)`
  > li {
    grid-template-columns: auto minmax(0, 2fr) minmax(90px, 1fr) minmax(90px, 1fr) auto;
  }
  @media (max-width: 800px) {
    > li {
      grid-template-columns: auto minmax(0, 1fr) auto;
    }
    .col-cat,
    .col-st {
      display: none;
    }
  }
  @media (max-width: 560px) {
    > li {
      grid-template-columns: auto minmax(0, 1fr);
      padding-block: 12px;
    }
    .acts {
      grid-column: 1 / -1;
      justify-content: flex-start;
    }
  }
  .acts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
`;
const Form = styled.div`
  display: grid;
  gap: 16px;
  .two {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    @media (max-width: 480px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`;

export default function Maps() {
  const { t } = useLanguage();
  const [items, setItems] = useState<Map[] | null>(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<(Omit<Map, "id"> & { id?: string }) | null>(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState<Map | null>(null);
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState("");
  const baseline = useRef("");

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await apiFetch("/admin/maps?page=1&pageSize=200");
      if (!res.ok) throw new Error(String(res.status));
      const d = await res.json();
      setItems((d.items || []).map(toMap));
    } catch (e) {
      setItems(null);
      setError((e as Error).message || "error");
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const open = (m?: Map) => {
    const next = m ? { ...m } : { ...EMPTY };
    baseline.current = JSON.stringify(next);
    setFormError("");
    setEdit(next);
  };
  async function save() {
    if (!edit || saving) return;
    if (!edit.name.trim() || !edit.display_name.trim()) return setFormError(t("adm.map_required"));
    setSaving(true);
    setFormError("");
    try {
      const body = { ...edit, name: edit.name.trim(), display_name: edit.display_name.trim() };
      delete (body as { id?: string }).id;
      const res = await apiFetch(edit.id ? `/admin/maps/${encodeURIComponent(edit.id)}` : "/admin/maps", { method: edit.id ? "PUT" : "POST", body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data?.error === "string" ? data.error : t("adm.load_error"));
      toast.success(t("adm.map_saved"));
      setEdit(null);
      await load();
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }
  async function activate(m: Map) {
    try {
      const { id, ...rest } = m;
      const res = await apiFetch(`/admin/maps/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify({ ...rest, status: "active" }) });
      if (!res.ok) throw new Error();
      toast.success(t("adm.map_saved"));
      await load();
    } catch {
      toast.error(t("adm.load_error"));
    }
  }
  async function remove() {
    if (!del) return;
    setDelBusy(true);
    setDelError("");
    try {
      const res = await apiFetch(`/admin/maps/${encodeURIComponent(del.id)}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data?.error === "string" ? data.error : t("adm.load_error"));
      toast.success(t("adm.map_deleted"));
      setDel(null);
      await load();
    } catch (e) {
      setDelError((e as Error).message);
    } finally {
      setDelBusy(false);
    }
  }

  const needle = q.trim().toLowerCase();
  const shown = (items || []).filter((m) => !needle || m.display_name.toLowerCase().includes(needle) || m.name.toLowerCase().includes(needle));
  const dirty = !!edit && JSON.stringify({ ...edit }) !== baseline.current;
  const set = (patch: Partial<Map>) => setEdit((e) => (e ? { ...e, ...patch } : e));

  return (
    <>
      <PageHeader
        title={t("adm.maps")}
        actions={
          <Action type="button" $variant="primary" onClick={() => open()}>
            <FiPlus aria-hidden="true" />
            {t("adm.maps_add")}
          </Action>
        }
      />
      <Surface aria-label={t("adm.maps")}>
        <Toolbar>
          <Field className="grow" type="search" aria-label={t("adm.maps_search")} placeholder={t("adm.maps_search")} value={q} onChange={(e) => setQ(e.target.value)} />
          <span className="count">{t("adm.total", { n: shown.length })}</span>
        </Toolbar>
        {error ? (
          <StateBlock kind="error" title={t("adm.load_error")} text={adminErrorText(error, t)} actionLabel={t("adm.retry")} onAction={load} />
        ) : !items ? (
          <div role="status" aria-label={t("adm.loading")} style={{ display: "grid", gap: 8 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} $h={60} $r={10} />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <StateBlock kind="empty" title={t("adm.maps_empty")} />
        ) : (
          <List>
            {shown.map((m) => (
              <li key={m.id}>
                <MapThumb map={m.name} />
                <div style={{ minWidth: 0 }}>
                  <div className="name">{m.display_name}</div>
                  <div className="sub">{m.name}{m.workshop_id ? ` · Workshop ${m.workshop_id}` : ""}</div>
                </div>
                <span className="col-cat sub">{t(`adm.cat.${m.category}`)}</span>
                <span className="col-st">
                  <Chip $tone={m.status === "active" ? "ok" : m.status === "pending" ? "warn" : "muted"}>{t(`adm.st.${m.status}`)}</Chip>
                </span>
                <span className="acts">
                  {m.status !== "active" && (
                    <Action type="button" $variant="ghost" onClick={() => activate(m)} aria-label={`${t("adm.maps_activate")}: ${m.display_name}`}>
                      {t("adm.maps_activate")}
                    </Action>
                  )}
                  <Action type="button" $variant="ghost" onClick={() => open(m)} aria-label={`${t("adm.maps_edit")}: ${m.display_name}`}>
                    {t("adm.maps_edit")}
                  </Action>
                  <Action type="button" $variant="ghost" onClick={() => { setDelError(""); setDel(m); }} aria-label={`${t("adm.maps_delete")}: ${m.display_name}`}>
                    {t("adm.maps_delete")}
                  </Action>
                </span>
              </li>
            ))}
          </List>
        )}
      </Surface>

      {edit && (
        <Dialog
          size="md"
          label={edit.id ? t("adm.maps_edit") : t("adm.maps_add")}
          onClose={() => setEdit(null)}
          dirty={dirty}
          footer={
            <>
              {formError && (
                <Err role="alert" style={{ marginRight: "auto" }}>
                  {formError}
                </Err>
              )}
              <Action type="button" onClick={() => setEdit(null)} disabled={saving}>
                {t("common.cancel")}
              </Action>
              <Action type="button" $variant="primary" onClick={save} disabled={saving || (!!edit.id && !dirty)} aria-busy={saving}>
                {t("profile.save")}
              </Action>
            </>
          }
        >
          <h2>{edit.id ? t("adm.maps_edit") : t("adm.maps_add")}</h2>
          <Form>
            <div className="two">
              <FormField>
                {t("adm.map_name")}
                <Field value={edit.name} onChange={(e) => set({ name: e.target.value })} />
              </FormField>
              <FormField>
                {t("adm.map_display")}
                <Field value={edit.display_name} onChange={(e) => set({ display_name: e.target.value })} />
              </FormField>
            </div>
            <FormField>
              {t("adm.map_desc")}
              <TextArea value={edit.description} onChange={(e) => set({ description: e.target.value })} style={{ minHeight: 80 }} />
            </FormField>
            <div className="two">
              <FormField>
                {t("adm.map_image")}
                <Field value={edit.image_url} onChange={(e) => set({ image_url: e.target.value })} />
              </FormField>
              <FormField>
                {t("adm.map_workshop")}
                <Field value={edit.workshop_id} onChange={(e) => set({ workshop_id: e.target.value })} />
              </FormField>
            </div>
            <div className="two">
              <FormField>
                {t("adm.map_category")}
                <Select value={edit.category} onChange={(e) => set({ category: e.target.value })}>
                  {["main", "reserve", "premium", "custom"].map((c) => (
                    <option key={c} value={c}>
                      {t(`adm.cat.${c}`)}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField>
                {t("adm.map_status")}
                <Select value={edit.status} onChange={(e) => set({ status: e.target.value })}>
                  {["active", "inactive", "pending"].map((c) => (
                    <option key={c} value={c}>
                      {t(`adm.st.${c}`)}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
            <div className="two">
              <FormField>
                {t("adm.map_min")}
                <Field type="number" min={1} value={edit.min_players} onChange={(e) => set({ min_players: Number(e.target.value) })} />
              </FormField>
              <FormField>
                {t("adm.map_max")}
                <Field type="number" min={1} value={edit.max_players} onChange={(e) => set({ max_players: Number(e.target.value) })} />
              </FormField>
            </div>
            <label style={{ display: "flex", gap: 10, alignItems: "center", minHeight: 44 }}>
              <input type="checkbox" checked={edit.is_official} onChange={(e) => set({ is_official: e.target.checked })} />
              {t("adm.map_official")}
            </label>
          </Form>
        </Dialog>
      )}
      {del && (
        <ConfirmDialog
          title={t("adm.map_delete_title", { name: del.display_name })}
          description={t("adm.map_delete_text")}
          confirmLabel={t("adm.maps_delete")}
          busy={delBusy}
          error={delError}
          onConfirm={remove}
          onClose={() => setDel(null)}
        />
      )}
    </>
  );
}
